import { forwardRef, useMemo } from "react";
import cx from "classnames";
import { Text } from "components/Typography/Text";
import { Avatar } from "components/Common/Avatar";
import { AvatarListContent } from "content";
import { getInitials } from "utils/common/initials";
import { TStudent } from "types/api/student";
import "./AvatarList.styles.scss";

type AvatarListProps = {
  /**
   * The user data - can be either:
   * - number: count of users (legacy format, shows generic avatars)
   * - TStudent[]: array of student data (shows actual photos and initials)
   */
  userData: number | TStudent[];
  /**
   * Number of `Avatar` components to show in the list.
   * @default '3'
   */
  avatarsToShow?: number;
  /**
   * The size of the avatar.
   * @default 'medium'
   */
  size?: "small" | "medium" | "large";
  /**
   * The text content of the `AvatarList` component if data length is `0`.
   */
  emptyText?: string;
  /**
   * Override or extend the styles applied to the component.
   */
  className?: string;
};

const AvatarList: React.ForwardRefRenderFunction<
  HTMLDivElement,
  AvatarListProps
> = (props, ref) => {
  const {
    userData,
    avatarsToShow = 3,
    size = "medium",
    emptyText = AvatarListContent.Default.EMPTY,
    className,
  } = props;

  const { avatarsAmount, label, students, isStudentArray } = useMemo(() => {
    const isStudentArray = Array.isArray(userData);
    const dataLength = isStudentArray ? userData.length : userData;
    const avatarsAmount =
      dataLength >= avatarsToShow ? avatarsToShow : Math.max(dataLength, 0);

    return {
      avatarsAmount,
      label: Math.max(dataLength - avatarsAmount, 0),
      students: isStudentArray ? userData.slice(0, avatarsToShow) : [],
      isStudentArray,
    };
  }, [userData, avatarsToShow]);

  return (
    <div
      ref={ref}
      className={cx([
        "nb-common-avatar-list",
        `nb-common-avatar-list--${size}`,
        className,
      ])}
    >
      {avatarsAmount === 0 ? (
        <Text variant="body2" className="nb-common-avatar-list-label">
          {emptyText}
        </Text>
      ) : (
        <>
          <div className="nb-common-avatar-list-wrapper">
            {isStudentArray
              ? students.map((student, index) => (
                  <Avatar
                    key={student.id}
                    size={size}
                    bordered
                    image={student.person.photoUrl}
                    initials={
                      !student.person.photoUrl
                        ? getInitials(
                            `${student.person.firstName} ${student.person.lastName}`
                          )
                        : undefined
                    }
                    className={cx("nb-common-avatar-list-item", {
                      "nb-common-avatar-list-item--overlap": index !== 0,
                    })}
                  />
                ))
              : Array(avatarsAmount)
                  .fill(null)
                  .map((_, index) => (
                    <Avatar
                      key={index}
                      size={size}
                      bordered
                      className={cx("nb-common-avatar-list-item", {
                        "nb-common-avatar-list-item--overlap": index !== 0,
                      })}
                    />
                  ))}
          </div>
          {label > 0 && (
            <Text variant="body2" className="nb-common-avatar-list-label">
              +{label}
            </Text>
          )}
        </>
      )}
    </div>
  );
};

/**
 * `AvatarList` is a common component to display array of user photos or emojis if first not provided
 */
export const ForwardedAvatarList = forwardRef(AvatarList);
